/**
 * createAuthContext — factory for portal-specific Auth context + provider + hook.
 *
 * Story #2560 Sprint 6 Task #2575 (C25). Eliminates per-portal AuthContext
 * duplication by deriving everything from a config object: storage keys,
 * demo-token prefix, check-interval, optional id-token enrichment, and
 * optional logout side-effect.
 *
 * Portal usage:
 *   const { AuthProvider, useAuth } = createAuthContext<MyAuthUser>({
 *     portalName: 'analysis',
 *     storageKeys: { authToken: 'authToken', idToken: 'idToken',
 *                    refreshToken: 'refreshToken', user: 'user' },
 *     enrichUserFromIdToken: (user, payload) => ({
 *       ...user,
 *       tier: parseTier(payload['custom:tier'])
 *     }),
 *     onLogout: () => performLogout({ storageKeys: [...], onClear }),
 *   });
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface BaseAuthUser {
  email?: string;
  username?: string;
}

export interface AuthStorageKeys {
  authToken: string;
  idToken: string;
  refreshToken: string;
  user: string;
}

/**
 * Auth driver determines where session credentials live.
 *
 * - `localStorage` (default): legacy behaviour, tokens in localStorage.
 *   The `login` function persists token + user; `logout` clears storage.
 *   Used by all 12 portals before the cookie migration (Story #2560 Task #2576).
 *
 * - `cookie`: Backend issues httpOnly cookies; client never touches the token.
 *   `login(token, user)` stores ONLY the user object (token slot is unused);
 *   `logout` clears the user object + invokes onLogout (which should call
 *   the cookie-clearing /auth/logout endpoint).
 *   Used by portals that have completed the cookie migration. The portal
 *   wires the actual login/refresh side-effects via `cookieAuth` driver
 *   on top of this Provider — this flag just changes the storage shape.
 */
export type AuthDriver = 'localStorage' | 'cookie';

export interface CreateAuthContextConfig<U extends BaseAuthUser = BaseAuthUser> {
  /** Portal identifier — used in error messages only. */
  portalName: string;
  /** Storage keys for the four auth artefacts. */
  storageKeys: AuthStorageKeys;
  /** Storage driver — see AuthDriver. Default: 'localStorage'. */
  driver?: AuthDriver;
  /** Token-prefix that always passes isTokenValid (e.g. 'demo-token-'). */
  demoTokenPrefix?: string;
  /** Periodic check interval for token expiry. Default 60000 ms. */
  checkIntervalMs?: number;
  /**
   * Optional hook to enrich the stored user from the id-token JWT payload.
   * Runs at init time when a stored user is present but missing fields.
   */
  enrichUserFromIdToken?: (user: U, idTokenPayload: Record<string, unknown>) => U;
  /**
   * Optional logout side-effect (e.g. performLogout, redirect).
   * Default: clear storage keys + redirect to /login.
   */
  onLogout?: () => void;
  /** Optional default user to fall back to when storedUser parsing fails. */
  fallbackUser?: U;
  /**
   * For driver='cookie': probe whether a session cookie is present.
   * Returns the parsed user (if known from a server-rendered hint or
   * a non-httpOnly companion cookie like `userInfo`), or null.
   * Default: returns null (the Provider stays unauthenticated until
   * an explicit login() call).
   */
  cookieSessionProbe?: () => U | null;
}

export interface AuthContextValue<U extends BaseAuthUser = BaseAuthUser> {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: U | null;
  login: (token: string, user?: U) => void;
  logout: () => void;
  checkAuth: () => boolean;
}

export interface CreatedAuthContext<U extends BaseAuthUser = BaseAuthUser> {
  AuthContext: React.Context<AuthContextValue<U> | undefined>;
  AuthProvider: React.FC<{ children: React.ReactNode }>;
  useAuth: () => AuthContextValue<U>;
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length === 3) {
      return JSON.parse(atob(parts[1])) as Record<string, unknown>;
    }
  } catch {
    /* invalid token */
  }
  return null;
};

export function createAuthContext<U extends BaseAuthUser = BaseAuthUser>(
  config: CreateAuthContextConfig<U>,
): CreatedAuthContext<U> {
  const {
    portalName,
    storageKeys,
    driver = 'localStorage',
    demoTokenPrefix,
    checkIntervalMs = 60_000,
    enrichUserFromIdToken,
    onLogout,
    fallbackUser,
    cookieSessionProbe,
  } = config;

  const AuthContext = createContext<AuthContextValue<U> | undefined>(undefined);

  const isTokenValid = (token: string): boolean => {
    try {
      const payload = decodeJwtPayload(token);
      if (payload && typeof payload.exp === 'number') {
        return Date.now() < payload.exp * 1000;
      }
      if (demoTokenPrefix && token.startsWith(demoTokenPrefix)) return true;
      return token.length > 0;
    } catch {
      return false;
    }
  };

  const clearAuthStorage = () => {
    localStorage.removeItem(storageKeys.authToken);
    localStorage.removeItem(storageKeys.idToken);
    localStorage.removeItem(storageKeys.refreshToken);
    localStorage.removeItem(storageKeys.user);
  };

  const defaultLogout = () => {
    clearAuthStorage();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  };

  const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<U | null>(null);

    const checkAuth = useCallback((): boolean => {
      if (driver === 'cookie') {
        // The httpOnly cookie can't be read from JS. We trust the in-memory
        // authenticated flag; expiry is enforced by the server (silent
        // refresh handles renewal — see scheduleTokenRefresh).
        return isAuthenticated;
      }
      const token = localStorage.getItem(storageKeys.authToken);
      if (!token) return false;
      return isTokenValid(token);
    }, [isAuthenticated]);

    useEffect(() => {
      const initLocalStorage = () => {
        const token = localStorage.getItem(storageKeys.authToken);
        const storedUser = localStorage.getItem(storageKeys.user);

        if (token && isTokenValid(token)) {
          setIsAuthenticated(true);
          if (storedUser) {
            try {
              let parsed = JSON.parse(storedUser) as U;
              if (enrichUserFromIdToken) {
                const idToken = localStorage.getItem(storageKeys.idToken);
                if (idToken) {
                  const payload = decodeJwtPayload(idToken);
                  if (payload) parsed = enrichUserFromIdToken(parsed, payload);
                }
              }
              localStorage.setItem(storageKeys.user, JSON.stringify(parsed));
              setUser(parsed);
            } catch {
              if (fallbackUser) setUser(fallbackUser);
            }
          }
        } else {
          clearAuthStorage();
          setIsAuthenticated(false);
          setUser(null);
        }
        setIsLoading(false);
      };

      const initCookie = () => {
        // Cookie driver: the httpOnly cookie cannot be inspected from JS.
        // We optionally probe a non-sensitive companion cookie (or a
        // server-rendered hint) for the user object; otherwise stay
        // unauthenticated until login() is explicitly called.
        const probed = cookieSessionProbe ? cookieSessionProbe() : null;
        if (probed) {
          setUser(probed);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
        setIsLoading(false);
      };

      if (driver === 'cookie') {
        initCookie();
      } else {
        initLocalStorage();
      }

      // localStorage driver runs a periodic exp-check; the cookie driver
      // delegates expiry to the server + scheduleTokenRefresh.
      if (driver === 'localStorage') {
        const interval = setInterval(() => {
          const token = localStorage.getItem(storageKeys.authToken);
          if (!token || !isTokenValid(token)) {
            setIsAuthenticated(false);
            setUser(null);
          }
        }, checkIntervalMs);
        return () => clearInterval(interval);
      }
      return undefined;
    }, []);

    const login = useCallback((token: string, userData?: U) => {
      if (driver === 'localStorage') {
        localStorage.setItem(storageKeys.authToken, token);
        if (userData) {
          localStorage.setItem(storageKeys.user, JSON.stringify(userData));
          setUser(userData);
        }
      } else if (userData) {
        // Cookie driver: token is held in the httpOnly cookie, never
        // persisted client-side. We do still cache the user object so
        // the UI has a name/role to show without a round-trip.
        setUser(userData);
      }
      setIsAuthenticated(true);
    }, []);

    const logout = useCallback(() => {
      if (onLogout) {
        onLogout();
      } else {
        defaultLogout();
      }
      setIsAuthenticated(false);
      setUser(null);
    }, []);

    return (
      <AuthContext.Provider
        value={{ isAuthenticated, isLoading, user, login, logout, checkAuth }}
      >
        {children}
      </AuthContext.Provider>
    );
  };

  const useAuth = (): AuthContextValue<U> => {
    const ctx = useContext(AuthContext);
    if (ctx === undefined) {
      throw new Error(
        `useAuth must be used within the AuthProvider for portal "${portalName}"`,
      );
    }
    return ctx;
  };

  return { AuthContext, AuthProvider, useAuth };
}
